'use client';
import { useState } from 'react';
import { useEvent } from '../common/useEvent';
import { IoIosWarning } from 'react-icons/io';
import PFParagraph from './PFParagraph';
import { IoIosArrowForward } from 'react-icons/io';
import Spinner from './Spinner';
import PFLink from './PFLink';

type Props = {
  jobs: { id: number; title: string; internal_job_id?: number }[];
  withJobSelecting?: boolean;
};

const defaultError =
  'Deine Bewerbung konnte gerade nicht abgeschickt werden, versuche es bitte später noch einmal.';

const JobsForm = (props: Props) => {
  const { jobs, withJobSelecting } = props;
  const [vorname, setVorname] = useState('');
  const [nachname, setNachname] = useState('');
  const [email, setEmail] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [formCompletitionStep, setFormCompletitionStep] = useState(0);
  const [selectedJob, setSelectedJob] = useState(
    jobs?.length > 0 ? props?.jobs[0]?.id : {},
  );
  const [error, setError] = useState<String | undefined>();
  const [loading, setLoading] = useState(false);

  const handleSubmit = useEvent(async () => {
    setLoading(true);
    const jobId = withJobSelecting ? selectedJob : jobs[0].internal_job_id;

    let postUrl = '';
    let getUrl = '';

    if (file) {
      const splittedFile = file.name.split('.');
      if (splittedFile.length < 2) {
        setLoading(false);
        setError('Die Datei muss eine .pdf oder .zip Dateierweiterung haben');
        return;
      }
      const fileExt = splittedFile[splittedFile.length - 1];
      if (fileExt !== 'pdf' && fileExt !== 'zip') {
        setLoading(false);
        setError('Die Datei muss eine .pdf oder .zip Dateierweiterung haben');
        return;
      }

      const presignedResponse = await fetch('/api/getPresignedUrl/', {
        method: 'POST',
        body: JSON.stringify({
          fileExtension: fileExt,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (presignedResponse.status !== 200) {
        setError(defaultError);
        setLoading(false);
        return;
      }
      const urls = await presignedResponse.json();
      postUrl = urls.postUrl;
      getUrl = urls.getUrl;
    }

    if (postUrl) {
      try {
        const amazonResponse = await fetch(postUrl, {
          method: 'PUT',
          body: file,
        });
        if (amazonResponse.status !== 200) {
          setError(defaultError);
          setLoading(false);
          return;
        }
      } catch (error) {
        setError(defaultError);
        setLoading(false);
        return;
      }
    }

    const body = {
      prospect: 'false',
      first_name: vorname,
      last_name: nachname,
      emails: [
        {
          email: email,
          type: 'personal',
        },
      ],
      job_id: jobId,
      external_id: Math.floor(Math.random() * 10000000),
      ...(getUrl && { resume: getUrl }),
    };

    fetch('/api/sendApplication', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          setError(defaultError);
          setLoading(false);
        }
      })
      .then((jsonData) => {
        if (jsonData.message === 'Application successful') {
          setVorname('');
          setNachname('');
          setEmail('');
          setFormCompletitionStep(2);
          setLoading(false);
        } else {
          setError(defaultError);
          setLoading(false);
        }
      })
      .catch(() => {
        setLoading(false);
        setError(defaultError);
      });
  });

  return (
    <div>
      {formCompletitionStep !== 2 && (
        <h2 className="mb-4 sm:text-4xl text-white">Jetzt bewerben</h2>
      )}
      {formCompletitionStep === 0 && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setFormCompletitionStep(1);
          }}
        >
          {withJobSelecting &&
            jobs?.map((job) => (
              <ul key={job.id} className="w-full sm:w-[50%]">
                <li
                  key={job.id}
                  className="w-full text-left mb-3 bg-white p-3 sm:px-5 sm:py-3 rounded-xl relative cursor-pointer"
                  onClick={() => setSelectedJob(job.id)}
                >
                  <div className="absolute top-[-64px] sm:top-0" />
                  <div className="flex content-center text-darkGrey items-center">
                    <input
                      type="radio"
                      name="job"
                      checked={selectedJob === job.id}
                      className="self-center cursor-pointer form-radio  mr-2 checked:!bg-darkGrey"
                      id={job.id}
                      onChange={() => setSelectedJob(job.id)}
                      required
                    />
                    <label
                      htmlFor={job.id}
                      className="flex-1 text-darkGrey sm:text-2xl cursor-pointer"
                    >
                      {job.title}
                    </label>
                    <PFLink
                      href={
                        '/jobs/' +
                        job?.title.toLowerCase().trim().replace(/\s+/g, '-') +
                        '/'
                      }
                      className="flex w-10 h-10 items-center justify-center"
                      title={`Zu ${job.title}`}
                    >
                      <IoIosArrowForward className="hover:cursor-pointer text-darkGrey w-8" />
                    </PFLink>
                  </div>
                </li>
              </ul>
            ))}
          <div className="absolute top-[-64px] sm:top-0" />
          <label
            className="text-white block py-1 sm:py-3 sm:text-2xl"
            htmlFor="name"
          >
            Vorname: <br />
          </label>
          <input
            required
            name="vorname"
            type="text"
            autoComplete="given-name"
            value={vorname}
            onChange={(e) => setVorname(e.target.value)}
            className="w-full sm:w-[50%] !text-darkGrey form-input sm:text-2xl rounded-xl"
            id="name"
          />{' '}
          <br />
          <label
            className="text-white block py-1 sm:py-3 sm:text-2xl"
            htmlFor="surname"
          >
            Nachname: <br />
          </label>
          <input
            required
            name="nachname"
            type="text"
            autoComplete="family-name"
            value={nachname}
            onChange={(e) => setNachname(e.target.value)}
            className="w-full sm:w-[50%] !text-darkGrey rounded-xl form-input sm:text-2xl"
            id="surname"
          />
          <br />
          <label
            className="text-white block py-1 sm:py-3 sm:text-2xl"
            htmlFor="email"
          >
            Email: <br />
          </label>
          <input
            required
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full sm:w-[50%] !text-darkGrey rounded-xl form-input sm:text-2xl"
            id="email"
          />
          <br />
          <label
            htmlFor="fileInput"
            className="text-white block py-1 sm:pt-3 sm:text-2xl cursor-pointer"
          >
            <PFParagraph className="sm:!text-2xl text-white">
              Lade hier deinen Lebenslauf als PDF hoch. Falls du mehrere Dateien
              anhängen möchtest, dann erstelle eine .zip Datei und lade sie hier
              hoch.
            </PFParagraph>
          </label>
          <input
            id="fileInput"
            type="file"
            name="file"
            accept=".pdf,.zip"
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              setFile(e.target.files[0]);
            }}
            className="max-w-full cursor-pointer text-white sm:text-2xl"
          />
          <div className="flex flex-row items-center gap-2 mt-2">
            <input
              required
              type="checkbox"
              name="dsgvo"
              className="w-6 h-6 cursor-pointer"
              id="link-checkbox"
            />
            <label
              htmlFor="link-checkbox"
              className="text-white block py-1 sm:py-3 sm:text-2xl cursor-pointer"
            >
              Ich akzeptiere die{' '}
              <PFLink
                href="/datenschutzerklaerung/"
                className="text-white hover:text-darkGrey underline underline-offset-4"
              >
                Datenschutzerklärung
              </PFLink>{' '}
              <br />
            </label>
          </div>
          <input
            type="submit"
            value="Abschicken"
            className="sm:mt-5  mt-4 rounded-xl w-full border-none text-primaryColor bg-white p-3 text-base sm:text-xl font-semibold  text-center self-center transition ease-in-out duration-300 sm:hover:bg-tintColorHover active:bg-tintColorHover active:text-white sm:hover:text-white sm:w-[50%] cursor-pointer"
          />
        </form>
      )}
      {formCompletitionStep === 1 && (
        <>
          <PFParagraph className="text-white child:sm:text-2xl">
            {withJobSelecting && (
              <>
                Job: <b>{jobs.find((job) => job.id === selectedJob)?.title}</b>
                <br />
              </>
            )}
            Vorname: <b>{vorname}</b> <br />
            Nachname: <b>{nachname}</b> <br />
            Email: <b>{email}</b> <br />
            {file && (
              <>
                <b>Anhang: </b>
                <span className="text-white" key={file.name}>
                  {file.name}
                </span>
              </>
            )}
            <span className="rounded block text-white font-bold sm:text-2xl">
              Sind die Angaben korrekt?
            </span>
          </PFParagraph>
          {error && (
            <div className="flex flex-row items-center gap-2 pb-3">
              <IoIosWarning className="text-white h-10 w-10" />
              <div className="rounded-xl block  text-white font-bold sm:text-2xl">
                {error}
              </div>
            </div>
          )}
          <button
            onClick={handleSubmit}
            className="block rounded-xl h-12 mb-5 w-full border-none text-primaryColor bg-white p-3 text-base sm:text-xl font-semibold text-center self-center transition ease-in-out duration-300 sm:enabled:hover:bg-tintColorHover enabled:active:bg-tintColorHover enabled:active:text-white sm:enabled:hover:text-white sm:w-[50%] enabled:cursor-pointer form-button"
            disabled={loading}
          >
            {loading && (
              <div className="w-full flex justify-center">
                <Spinner />
              </div>
            )}
            {!loading && 'Abschicken'}
          </button>
          <button
            onClick={() => {
              setFormCompletitionStep(0);
              setError(defaultError);
              setFile(null);
            }}
            className="block rounded-xl h-12 mb-5 w-full border-none text-primaryColor bg-white p-3 text-base sm:text-xl font-semibold text-center self-center transition ease-in-out duration-300 sm:enabled:hover:bg-tintColorHover enabled:active:bg-tintColorHover enabled:active:text-white sm:enabled:hover:text-white sm:w-[50%] enabled:cursor-pointer form-button disabled:opacity-80"
            disabled={loading}
          >
            Bearbeiten
          </button>
        </>
      )}
      {formCompletitionStep === 2 && (
        <h2 className="text-white">
          Danke für deine Bewerbung, wir melden uns bald!
        </h2>
      )}
    </div>
  );
};

export default JobsForm;
